import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import isStrongPassword from 'validator/lib/isStrongPassword';
import isEmail from 'validator/es/lib/isEmail';

import { SignUpBody } from 'services/auth.service';
import { useAppDispatch } from 'store/store-hooks';
import { Path, useAuthNavigator } from 'components/_hooks/use-router';
import { signUp } from 'components/auth/auth.slice';

import { InputGroup } from 'components/UI/input/input-group';
import { InputBase } from 'components/UI/input/elements/input-base';
import { InputCheck } from 'components/UI/input/elements/input-check';
import { Button } from 'components/UI/button/button';

import styles from '../_auth.module.scss';
import { InputAlert } from 'components/UI/input/elements/input-alert';

interface SignUpForm extends SignUpBody {
  confirmPassword: string;
  acceptedAgreement?: boolean;
}

const AuthReg: React.FC = () => {
  const [isPending, setIsPending] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { navigateTo } = useAuthNavigator();
  const {
    control,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpForm>();

  const onSubmit: SubmitHandler<SignUpForm> = async (data) => {
    const urlParams = new URLSearchParams(window.location.search);
    const reqData: SignUpBody = {
      firstName: data.firstName,
      lastName: data.lastName,
      userName: data.userName,
      password: data.password,
      email: data.email,
      rdoAccountType: '0', // TODO: need to account type
      dob: data.dob,
      source: urlParams.get('s') || null,
    };
    try {
      setIsPending(true);
      const signUpRes = await dispatch(signUp(reqData)).unwrap();
      if (signUpRes.executionResult) {
        navigateTo(Path.activateAcc, `?email=${data.email}&username=${data.userName}`);
      } else toast.error('Something went wrong');
    } finally {
      setIsPending(false);
    }
  };

  const autocompleteOff = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.readOnly = false;
  };

  return (
    <div className={styles.Register}>
      <h3 className={styles.title}>Sign Up</h3>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.RegisterBody}>
          <InputGroup header="First Name" error={errors.firstName?.message} className={styles.RegisterInput}>
            <InputBase
              placeholder="Enter First Name"
              readOnly
              onFocus={autocompleteOff}
              {...register('firstName', {
                required: 'The First Name is required',
                validate: (value) =>
                  (value.length >= 2 && value.length <= 40) || 'The First Name must be 2—40 characters long',
              })}
              alert={!!errors.firstName?.message}
            />
          </InputGroup>
          <InputGroup header="Last Name" error={errors.lastName?.message} className={styles.RegisterInput}>
            <InputBase
              placeholder="Enter Last Name"
              readOnly
              onFocus={autocompleteOff}
              {...register('lastName', {
                required: 'The Last Name is required',
                validate: (value) =>
                  (value.length >= 2 && value.length <= 40) || 'The Last Name must be 2—40 characters long',
              })}
              alert={!!errors.lastName?.message}
            />
          </InputGroup>
          <InputGroup header="Email" error={errors.email?.message} className={styles.RegisterInput}>
            <InputBase
              placeholder="Enter Email"
              readOnly
              onFocus={autocompleteOff}
              {...register('email', {
                required: 'The Email is required',
                validate: (value) => isEmail(value) || 'The Email is invalid',
              })}
              alert={!!errors.email?.message}
            />
          </InputGroup>
          {/* TODO: Need to check the user name in the database */}
          <InputGroup header="Username" className={styles.RegisterInput} error={errors.userName?.message}>
            <InputBase
              placeholder="Enter Username"
              readOnly
              onFocus={autocompleteOff}
              {...register('userName', {
                required: 'The Username is required',
                validate: (value) =>
                  (value.length >= 2 && value.length <= 40) || 'The User Name must be 2—40 characters long',
              })}
              alert={!!errors.userName?.message}
            />
          </InputGroup>
          <InputGroup header="Date of Birth" className={styles.RegisterInput} error={errors.dob?.message}>
            <InputBase
              type="date"
              onFocus={autocompleteOff}
              {...register('dob', {
                required: 'Date of Birth is required',
                validate: (value) => {
                  const curr = new Date(value);
                  const min = new Date('1900-01-01');
                  const max = new Date();
                  max.setFullYear(max.getFullYear() - 13);
                  if (min.getTime() > curr.getTime()) return 'The date should be after 1900-01-01';
                  if (max.getTime() < curr.getTime()) return 'You must be at least 13 years old to use Pulse.';
                },
              })}
              alert={!!errors.dob?.message}
            />
          </InputGroup>
          <InputGroup header="Password" error={errors.password?.message} className={styles.RegisterInput}>
            <InputBase
              type="password"
              placeholder="Enter your password"
              {...register('password', {
                required: 'The Password is required',
                validate: (value) =>
                  isStrongPassword(value) || 'Requirements: lower/upper case, letters, numbers/symbols',
              })}
              alert={!!errors.password?.message}
            />
          </InputGroup>
          <InputGroup
            header="Confirm Password"
            error={errors.confirmPassword?.message}
            className={styles.RegisterInput}
          >
            <InputBase
              type="password"
              placeholder="Confirm your password"
              {...register('confirmPassword', {
                required: 'The Password is required',
                validate: (value) => getValues('password') === value || "Passwords don't match",
              })}
              alert={!!errors.confirmPassword?.message}
            />
          </InputGroup>
        </div>
        <div className={styles.Register__check}>
          <h3>
            <Controller
              name="acceptedAgreement"
              control={control}
              rules={{ required: 'The Agreement is required' }}
              render={({ field }) => (
                <InputCheck title="" setValue={() => field.onChange(!field.value)} value={!!field.value} />
              )}
            />
            <span>
              I agree to the{' '}
              <a href="https://reflectography.com/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="https://reflectography.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </h3>
          <InputAlert>{errors.acceptedAgreement?.message}</InputAlert>
        </div>

        <div className={styles.RegisterFooter}>
          <Button disabled={isPending || !!Object.keys(errors).length}>Create Account</Button>
          <p>
            Already have an account? <Link to={Path.login}>Sign In</Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export { AuthReg };
